import { memo, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { Label } from "app/pages/digital-marketing/keywords-campaigns/shared/gsc-import/ImportFromGSC/styles";

import type { FixedGscConfig, SavedGscMarket } from "../types";

import { AddMarketButton } from "./AddMarketButton";
import { GoogleMarket } from "./GoogleMarket";

type Props = {
    connectionId: number;
    site: string;
    languagesList: ISupportedLanguagesAndLocale[];
    fixedConfig?: FixedGscConfig;
    savedMarkets: Map<number, SavedGscMarket>;
    isAddingMarket: boolean;
    onSaveMarket: (market: SavedGscMarket, configIndex: number) => void;
    onRemoveMarket: (configIndex: number) => void;
    onStartAdding: () => void;
    onCancelAdding: () => void;
    onSavedMarketEditingChange: (configIndex: number, isEditing: boolean) => void;
};

export const GoogleMarketList = memo(
    ({
        connectionId,
        site,
        languagesList,
        fixedConfig,
        savedMarkets,
        isAddingMarket,
        onSaveMarket,
        onRemoveMarket,
        onStartAdding,
        onCancelAdding,
        onSavedMarketEditingChange,
    }: Props) => {
        const { translate } = useTranslate();

        const savedIndices = useMemo(() => [...savedMarkets.keys()], [savedMarkets]);

        // fixedConfig flow only: find the first market in the config array that hasn't been saved yet.
        // Returns -1 when there's no fixedConfig (GSC-only flow has no preset list to exhaust).
        const nextUnsavedIndex = useMemo(() => {
            if (!fixedConfig) {
                return -1;
            }
            return fixedConfig.marketConfig.findIndex((_, i) => !savedMarkets.has(i));
        }, [fixedConfig, savedMarkets]);

        // fixedConfig: limited to the number of preset markets. GSC-only: always allow adding more.
        const canAddMore = fixedConfig ? nextUnsavedIndex !== -1 : true;

        // Stable React key for the unsaved market form.
        // - fixedConfig flow: use the config array index so the form pre-selects the right market.
        // - GSC-only flow: use max(existing keys) + 1 to guarantee a unique key that won't
        //   collide with previously saved markets (prevents React from reusing stale state).
        const nextMarketKey = useMemo(() => {
            if (fixedConfig) {
                return nextUnsavedIndex;
            }
            return savedMarkets.size === 0 ? 0 : Math.max(...savedMarkets.keys()) + 1;
        }, [fixedConfig, nextUnsavedIndex, savedMarkets]);

        const totalMarkets = savedMarkets.size + (isAddingMarket ? 1 : 0);
        // At least one market must remain visible, so we only allow deleting a saved market
        // when there are 2+ markets in the list (saved + the unsaved form counts too).
        const canSavedMarketBeDeleted = totalMarkets > 1;
        // The unsaved form can be dismissed only when there's already a saved market,
        // so the user always sees at least one market.
        const canNewMarketBeDeleted = savedMarkets.size > 0;

        const allSavedMarketValues = useMemo(() => [...savedMarkets.values()], [savedMarkets]);

        // Pre-compute "other markets" for each saved key so we don't create new arrays on every render
        const otherMarketsPerKey = useMemo(() => {
            const result = new Map<number, SavedGscMarket[]>();
            const entries = [...savedMarkets.entries()];
            for (const [key] of entries) {
                result.set(
                    key,
                    entries.filter(([k]) => k !== key).map(([, m]) => m),
                );
            }
            return result;
        }, [savedMarkets]);

        // Precompute one stable delete callback per saved market key so memoized GoogleMarket
        // rows receive stable function props across parent renders.
        const savedMarketDeleteHandlers = useMemo(() => {
            const result = new Map<number, () => void>();
            for (const key of savedMarkets.keys()) {
                result.set(key, () => onRemoveMarket(key));
            }
            return result;
        }, [savedMarkets, onRemoveMarket]);

        // Stable per-row editing callbacks so memoized GoogleMarket rows keep referential identity.
        const savedMarketEditingHandlers = useMemo(() => {
            const result = new Map<number, (isEditing: boolean) => void>();
            for (const key of savedMarkets.keys()) {
                result.set(key, (isEditing: boolean) => onSavedMarketEditingChange(key, isEditing));
            }
            return result;
        }, [savedMarkets, onSavedMarketEditingChange]);

        return (
            <div>
                <Label>{translate("rankTracker.gsc.googleMarkets.label")}</Label>
                <Flex
                    flexDirection="column"
                    gap="12px"
                >
                    {[...savedMarkets.entries()].map(([key, market]) => (
                        <GoogleMarket
                            key={key}
                            defaultConfigIndex={key}
                            marketConfigs={fixedConfig?.marketConfig}
                            isDropdownLocked={!!fixedConfig}
                            connectionId={connectionId}
                            site={site}
                            languagesList={languagesList}
                            savedMarket={market}
                            otherSavedMarkets={fixedConfig ? undefined : otherMarketsPerKey.get(key)}
                            onEditingChange={savedMarketEditingHandlers.get(key)}
                            onSave={onSaveMarket}
                            onDelete={canSavedMarketBeDeleted ? savedMarketDeleteHandlers.get(key) : undefined}
                        />
                    ))}

                    {/* New (unsaved) market — must respect ALL saved markets as constraints */}
                    {isAddingMarket ? (
                        <GoogleMarket
                            key={`new-${nextMarketKey}`}
                            defaultConfigIndex={nextMarketKey}
                            marketConfigs={fixedConfig?.marketConfig}
                            disabledConfigIndices={savedIndices}
                            connectionId={connectionId}
                            site={site}
                            languagesList={languagesList}
                            otherSavedMarkets={fixedConfig ? undefined : allSavedMarketValues}
                            onSave={onSaveMarket}
                            // "Delete" on a new market means cancel adding — only allowed when there's a saved market
                            onDelete={canNewMarketBeDeleted ? onCancelAdding : undefined}
                        />
                    ) : null}

                    <AddMarketButton
                        isDisabled={isAddingMarket || !canAddMore}
                        onClick={onStartAdding}
                    />
                </Flex>
            </div>
        );
    },
);
